import type { ReactNode } from "react";

import { cn } from "../utils/cn";

/**
 * The head of a tab or a page section in the bold design: a small kicker in the accent colour,
 * a large display title and one line of explanation. Every tab uses it, so a
 * change to how sections open is one change here.
 *
 * The title is a node so a word can be set in italic or semibold with an
 * `<em>`. It is an h2 by default, because the application shell already holds
 * the page's only h1.
 */
export function SectionHeader({
  kicker,
  title,
  subline,
  as: Heading = "h2",
  className,
  testId,
}: {
  kicker?: string;
  title: ReactNode;
  subline?: ReactNode;
  as?: "h2" | "h3";
  className?: string;
  testId?: string;
}) {
  return (
    <header
      className={cn("flex min-w-0 flex-col gap-1.5", className)}
      data-testid={testId}
    >
      {kicker && (
        <p
          className="text-[13px] font-semibold uppercase tracking-[0.16em] text-lit"
          data-testid="section-header-kicker"
        >
          {kicker}
        </p>
      )}
      <Heading className="font-display text-[2.5rem] font-normal leading-[1.05] tracking-tight text-foreground wrap-anywhere [&_em]:font-semibold">
        {title}
      </Heading>
      {subline && (
        <p
          className="text-base text-muted-foreground"
          data-testid="section-header-subline"
        >
          {subline}
        </p>
      )}
    </header>
  );
}
